import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Phone, Building2, MapPin, User, Package, Loader2, Search } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DetailsPage() {
  const { type, orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState(null);
  const [orderRef, setOrderRef] = useState("");
  const [error, setError] = useState(null);

  const isDestinataire = type === "destinataire";

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    fetchOrderDetails();
  }, [orderId, type, navigate]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      const orderData = response.data;
      
      setOrderRef(orderData.reference || `#${orderId}`);
      
      // Select the appropriate address based on type
      if (isDestinataire) {
        setAddress(orderData.delivery_address);
      } else {
        setAddress(orderData.invoice_address);
      }
    } catch (err) {
      console.error("Order fetch error:", err);
      setError(err.response?.data?.detail || "Erreur lors de la récupération");
      toast.error("Erreur lors de la récupération des détails");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/result/${orderId}`);
  };

  const handleCall = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  const formatPhoneDisplay = (phone) => {
    if (!phone) return null;
    // Format for display (French format)
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
    }
    return phone;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="w-16 h-16 text-[#e5007c] animate-spin" />
        <p className="mt-4 text-xl font-bold uppercase tracking-wider">
          Chargement...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <header className="p-4 border-b-2 border-black">
          <Button
            onClick={() => navigate("/search")}
            variant="ghost"
            className="h-12 px-4 font-bold uppercase tracking-wide border-2 border-black hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            RETOUR
          </Button>
        </header>
        <main className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="text-center">
            <p className="text-xl text-red-600 font-bold">{error}</p>
            <Button
              onClick={() => navigate("/search")}
              className="btn-primary mt-6"
            >
              NOUVELLE RECHERCHE
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header 
        className={`p-4 border-b-2 border-black flex items-center justify-between ${
          isDestinataire ? 'bg-[#e5007c] text-white' : 'bg-black text-white'
        }`}
      >
        <Button
          onClick={handleBack}
          variant="ghost"
          className={`h-12 px-4 font-bold uppercase tracking-wide border-2 ${
            isDestinataire 
              ? 'border-white text-white hover:bg-[#c4006a]' 
              : 'border-[#e5007c] text-white hover:bg-gray-800'
          }`}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          RETOUR
        </Button>
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-wider opacity-80">
            Commande
          </p>
          <p className="text-lg font-black">{orderRef}</p>
        </div>
        <Button
          onClick={() => navigate("/search")}
          className={`h-12 px-4 font-bold uppercase tracking-wide border-2 ${
            isDestinataire 
              ? 'bg-white text-[#e5007c] border-white hover:bg-gray-100' 
              : 'bg-[#e5007c] text-white border-[#e5007c] hover:bg-[#c4006a]'
          }`}
          data-testid="other-order-btn"
        >
          <Search className="w-5 h-5 mr-2" />
          <span className="hidden sm:inline">AUTRE</span>
        </Button>
      </header>

      {/* Title Section */}
      <div 
        className={`p-6 border-b-2 border-black ${
          isDestinataire ? 'bg-[#e5007c] text-white' : 'bg-black text-white'
        }`}
      >
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 flex items-center justify-center border-2 ${
            isDestinataire 
              ? 'bg-white border-black' 
              : 'bg-white border-[#e5007c]'
          }`}>
            {isDestinataire ? (
              <Package className="w-8 h-8 text-[#e5007c]" />
            ) : (
              <User className="w-8 h-8 text-black" />
            )}
          </div>
          <div>
            <h1 
              className="text-3xl font-black uppercase tracking-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              {isDestinataire ? 'DESTINATAIRE' : 'EXPEDITEUR'}
            </h1>
            <p className="text-sm uppercase tracking-wider opacity-80">
              {isDestinataire ? 'Adresse de livraison' : 'Adresse de facturation'}
            </p>
          </div>
        </div>
      </div>

      {/* Address Details */}
      <main className="flex-1 p-6">
        <div className="max-w-md mx-auto space-y-6 animate-slide-up">
          {/* Company */}
          {address?.company && (
            <div className="card-brutalist">
              <div className="flex items-start gap-4">
                <Building2 className="w-6 h-6 text-[#e5007c] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
                    Société
                  </p>
                  <p className="text-xl font-bold" data-testid="company-name">
                    {address.company}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Name */}
          <div className="card-brutalist">
            <div className="flex items-start gap-4">
              <User className="w-6 h-6 text-[#e5007c] flex-shrink-0 mt-1" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
                  Nom / Prénom
                </p>
                <p className="text-xl font-bold" data-testid="contact-name">
                  {address?.lastname} {address?.firstname}
                </p>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="card-brutalist">
            <div className="flex items-start gap-4">
              <MapPin className="w-6 h-6 text-[#e5007c] flex-shrink-0 mt-1" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
                  Adresse
                </p>
                <p className="text-lg font-medium" data-testid="address-line1">
                  {address?.address1}
                </p>
                {address?.address2 && (
                  <p className="text-lg font-medium text-gray-600" data-testid="address-line2">
                    {address.address2}
                  </p>
                )}
                <p className="text-lg font-bold mt-1" data-testid="address-city">
                  {address?.postcode} {address?.city}
                </p>
              </div>
            </div>
          </div>

          {/* Phone Numbers - Click to Call */}
          <div className="space-y-4 mt-8">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 text-center">
              Appeler
            </p>

            {address?.phone && (
              <button
                onClick={() => handleCall(address.phone)}
                className="phone-call-btn relative"
                data-testid="call-phone"
              >
                <Phone className="w-8 h-8" />
                <div className="flex-1 text-left">
                  <p className="text-xs uppercase opacity-80">Téléphone fixe</p>
                  <p className="text-xl font-black tracking-wider">
                    {formatPhoneDisplay(address.phone)}
                  </p>
                </div>
              </button>
            )}

            {address?.phone_mobile && (
              <button
                onClick={() => handleCall(address.phone_mobile)}
                className="phone-call-btn relative"
                data-testid="call-mobile"
              >
                <Phone className="w-8 h-8" />
                <div className="flex-1 text-left">
                  <p className="text-xs uppercase opacity-80">Mobile</p>
                  <p className="text-xl font-black tracking-wider">
                    {formatPhoneDisplay(address.phone_mobile)}
                  </p>
                </div>
              </button>
            )}

            {!address?.phone && !address?.phone_mobile && (
              <div className="p-6 bg-gray-100 border-2 border-gray-300 text-center">
                <Phone className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 font-medium uppercase">
                  Aucun numéro disponible
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Bottom Navigation */}
      <footer className="p-4 border-t-2 border-black">
        <Button
          onClick={handleBack}
          className="btn-secondary w-full"
          data-testid="back-to-choice"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          RETOUR AU CHOIX
        </Button>
      </footer>
    </div>
  );
}
